import React from 'react';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { CheckSquare, X, Pin, PinOff } from 'lucide-react';

/**
 * Bulk Selection Bar Component
 * Shows when items are selected in a table, providing bulk action buttons
 *
 * @param {number} selectedCount - Number of selected items
 * @param {Array} actions - Array of action configurations { label, onClick, className, testId }
 * @param {Function} onClearSelection - Callback to clear all selections
 * @param {boolean} filterClearSelection - Iter 265.3 — When true, changing a
 *   dashboard filter deselects rows (opt-in). Default (false) keeps
 *   selection sticky across filter changes — dispatchers rely on this
 *   while triaging.
 * @param {Function} onToggleFilterClear - Toggle callback (persisted to
 *   backend user_preferences).
 */
export default function BulkSelectionBar({
  selectedCount,
  actions = [],
  onClearSelection,
  show = true,
  filterClearSelection,
  onToggleFilterClear,
}) {
  if (!show || selectedCount === 0) return null;
  const sticky = !filterClearSelection;
  return (
    <Card className="bg-blue-900/30 border-blue-700 p-3" data-testid="bulk-selection-bar">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          <CheckSquare className="h-5 w-5 text-blue-400" />
          <span className="text-white font-medium">{selectedCount} selected</span>
          {typeof onToggleFilterClear === 'function' && (
            <Button
              variant="ghost"
              size="sm"
              onClick={onToggleFilterClear}
              className={`h-7 px-2 gap-1 ${sticky ? 'text-emerald-300 hover:text-emerald-200' : 'text-slate-400 hover:text-white'}`}
              data-testid="bulk-sticky-toggle"
              title={sticky
                ? 'Selection is sticky — filter changes keep rows selected. Click to make filters auto-clear selection.'
                : 'Selection auto-clears on filter change. Click to keep selection sticky.'}
            >
              {sticky
                ? <><Pin className="h-3.5 w-3.5" /> <span className="text-xs">Sticky</span></>
                : <><PinOff className="h-3.5 w-3.5" /> <span className="text-xs">Auto-clear</span></>
              }
            </Button>
          )}
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          {actions.map((action, index) => (
            <Button
              key={index}
              variant="outline"
              size="sm"
              onClick={action.onClick}
              className={action.className || "border-blue-600 text-blue-300 hover:bg-blue-600"}
              data-testid={action.testId}
            >
              {action.icon && <action.icon className="h-4 w-4 mr-1" />}
              {action.label}
            </Button>
          ))}
          <Button
            variant="ghost"
            size="sm"
            onClick={onClearSelection}
            className="text-slate-400 hover:text-white"
            data-testid="clear-selection-btn"
          >
            <X className="h-4 w-4" />
          </Button>
        </div>
      </div>
    </Card>
  );
}
